import * as React from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography } from '@mui/material';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import RuleIcon from '@mui/icons-material/Rule';

import schemaJson from './finalthesis.schema.json';
import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

import { fillInJsxParagraphed, TranslateFunction } from './I18n';
import * as Data from './DataRepresentation';
import * as Form from './finalthesis';



// Define a type for the component props
interface HelpDialogProps {

	open: boolean,
	setOpen: React.Dispatch<React.SetStateAction<boolean>>,
	translator: TranslateFunction,
	formData: Form.AnmeldungDerAbschlussarbeit
}

function isJSONSchema7(schema: JSONSchema7Definition): schema is JSONSchema7 {
	return schema ? (schema as JSONSchema7).properties !== undefined : false;
}


// Use the 'FC' (Functional Component) generic from React, with our props type
const HelpDialog: React.FC<HelpDialogProps> = ({ open, setOpen, translator, formData }: HelpDialogProps) => {

	const handleClose = () => {
		setOpen(false);
	};

	const descriptionElementRef = React.useRef<HTMLElement>(null);
	React.useEffect(() => {
		if (open) {
			const { current: descriptionElement } = descriptionElementRef;
			if (descriptionElement !== null) {
				descriptionElement.focus();
			}
		}
	}, [open]);

	const schema = schemaJson as JSONSchema7;
	
	const formKeys = [];
	
	for (const key in schema.properties) {
		var property = schema.properties[key];
		if (property === undefined || (isJSONSchema7(property) && property.type === 'object')) {
			continue;
		}
		formKeys.push(key);
	}

	var requirementsPropertyName = Data.getRequirementsPropertyName(formData);
	if (requirementsPropertyName === undefined) {
		requirementsPropertyName = 'requirements_other';
	}

	if (schema.properties !== undefined) {
		var requirementsDefinition = schema.properties[requirementsPropertyName] as JSONSchema7Definition;
		if (isJSONSchema7(requirementsDefinition)) {
			for (const key in requirementsDefinition.properties) {
				formKeys.push(requirementsPropertyName + "." + key);
			}
		}
	}

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleClose}
				scroll={'paper'}
				aria-labelledby="help-dialog-title"
				aria-describedby="help-dialog-description"
			>
				<DialogTitle id="dialog-title">{translator("page.dialog.help.title")}</DialogTitle>
				<DialogContent dividers={true}>
					<DialogContentText
						id="help-dialog-description"
						ref={descriptionElementRef}
						tabIndex={-1}
					>
					{fillInJsxParagraphed(translator("page.dialog.help.text"), {
						link_icon: (<LinkIcon aria-label={translator("aria.link_icon")} style={{verticalAlign:'middle'}} />),
						download_icon: (<FileDownloadIcon aria-label={translator("aria.download_icon")} style={{verticalAlign:'middle'}} />),
						print_icon: (<PrintIcon aria-label={translator("aria.prt=int_icon")} style={{verticalAlign:'middle'}} />),
						validation_icon: (<RuleIcon aria-label={translator("aria.validation_icon")} style={{verticalAlign:'middle'}} />)
					})}
						

						<Typography variant="h5">
							{translator("page.dialog.help.descriptions.title")}
						</Typography>

						<TableContainer component={Paper}>
							<Table aria-label={translator("aria.dialog.help.description_table")}>
								<TableHead>
									<TableRow>
										<TableCell>{translator("page.dialog.help.column_field")}</TableCell>
										<TableCell>{translator("page.dialog.help.column_description")}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
								{
									formKeys.map((key) =>
										(<TableRow>
										<TableCell>{translator(key + ".label")}</TableCell>
										<TableCell>{translator(key + ".description")}</TableCell>
										</TableRow>)
									)
								}
								</TableBody>
							</Table>
						</TableContainer>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>{translator("page.dialog.ok")}</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}
export default HelpDialog;